import { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { getFixcenterProductLevelAttributes } from '../../../services/products.service';
import { FeatureType } from '../../../shared/models/feature-type.model';
import OrderingRangeSlider from './OrderingRangeSlider';
import OrderingDropdownFilter from './OrderingDropdownFilter';
import { IFeature } from '../../product-filters-menu/interfaces/feature.interface';
import { IFeatureDiameterDto } from '../../product-filters-menu/interfaces/feature-diameter-dto.interface';
import { IProductLevelFilteredFeatureDto } from '../../product-filters-menu/interfaces/product-level-filtered-feature-dto.interface';
import OrderingDiameterFilter from './OrderingDiameterFilter';
import { diameterFilterExist } from '../../product-filters-menu/diameter-filter.service';
import { useBetween } from 'use-between';
import { ProductFiltersUseBetween } from '../../product-filters-menu/use-between/product-filters-use-between';
import { getRangeFilter } from '../../product-filters-menu/product-filters.service';
import OrderingTableFiltersSkeleton from './OrderingTableFiltersSkeleton';
import { ProductFeatureDetailsDto } from '../../../shared/models/product-feature-details-dto.model';
import { hasUsableFeatureValue } from '../../../shared/services/product-feature-filter.service';
import { filterItemSx, filterLabelSx } from './ordering-table-filters.styles';

// Features for which at least one VISIBLE variant in this family has no usable value - only these get the
// "Not configured" checkbox in their dropdown (see OrderingDropdownFilter). Scoped to visibleProductIds when
// given: a variant with On_Website=false never appears in the table, so its own gaps must not trigger a
// checkbox the customer can never connect to a row they can actually see.
// A visible variant that is absent from variantFeatureValues has no value for any feature, so it counts as missing.
const computeFeatureCodesWithMissingValues = (
    features: IFeature[],
    variantFeatureValues: Record<string, ProductFeatureDetailsDto[]>,
    visibleProductIds?: string[]
): Set<string> => {
    const valuesByVariantId = new Map<string, ProductFeatureDetailsDto[]>(
        Object.entries(variantFeatureValues).map(([id, details]) => [id.toLowerCase(), details])
    );
    const variantIds =
        visibleProductIds && visibleProductIds.length > 0
            ? visibleProductIds.map((id) => id.toLowerCase())
            : [...valuesByVariantId.keys()];
    const result = new Set<string>();
    if (variantIds.length === 0) {
        return result;
    }
    for (const feature of features) {
        const everyVariantHasValue = variantIds.every((id) =>
            (valuesByVariantId.get(id) ?? []).some(
                (d) => d.featureCode === feature.code && hasUsableFeatureValue(d.value)
            )
        );
        if (!everyVariantHasValue) {
            result.add(feature.code);
        }
    }
    return result;
};

export interface OrderingTableFiltersProps {
    productId: string;
    categoryId: string;
    onFiltersLoaded: (
        filters: IFeature[],
        variantFeatureValues: Record<string, ProductFeatureDetailsDto[]>,
        diameterFilter?: IFeatureDiameterDto
    ) => void;
    // Ids of the products actually rendered in the table (already filtered by On_Website upstream) - see
    // computeFeatureCodesWithMissingValues.
    visibleProductIds?: string[];
    // True once the parent's own product fetch (which visibleProductIds comes from) has resolved. Rendering
    // is held back on this too (see isSkeletonLoader below) - without it, OrderingDropdownFilter's mount
    // effect captures its default "select all" value (including whether "Not configured" belongs in it)
    // before visibleProductIds arrives, permanently freezing every feature's default filter at
    // hasVariantsWithoutValue=false regardless of the real, later-resolved answer.
    productsLoaded?: boolean;
}

export default function OrderingTableFilters({
    productId,
    categoryId,
    onFiltersLoaded,
    visibleProductIds,
    productsLoaded,
}: OrderingTableFiltersProps) {
    const [filters, setFilters] = useState<IFeature[]>([]);
    const [diameterFilter, setDiameterFilter] = useState<IFeatureDiameterDto>();
    const [isSkeletonLoader, setIsSkeletonLoader] = useState<boolean>(false);
    const [variantFeatureValues, setVariantFeatureValues] = useState<Record<string, ProductFeatureDetailsDto[]>>({});
    const { setFeaturesFilters } = useBetween(ProductFiltersUseBetween);

    useEffect(() => {
        if (productId && categoryId) {
            fetchProductLevelAttributes();
        }
    }, [productId, categoryId]);

    // Computed during render (not via a useEffect + state) so it is available to OrderingDropdownFilter in the
    // SAME pass it first mounts in - an effect-based version lags one render behind, so its "default select
    // all" mount effect would capture a stale (always false) hasVariantsWithoutValue before visibleProductIds
    // (a separate, independently-resolving fetch in the parent) ever arrives.
    const featureCodesWithMissingValues = useMemo(
        () => computeFeatureCodesWithMissingValues(filters, variantFeatureValues, visibleProductIds),
        [filters, variantFeatureValues, visibleProductIds]
    );

    const fetchProductLevelAttributes = () => {
        setIsSkeletonLoader(true);
        getFixcenterProductLevelAttributes(productId, categoryId).then((response: any): void => {
            const dto: IProductLevelFilteredFeatureDto = response.data;
            const data = (dto.data ?? []) as IFeature[];
            const diameter = dto.diameter as IFeatureDiameterDto;
            const values = dto.variantFeatureValues ?? {};
            setFilters(data);
            setDiameterFilter(diameter);
            setVariantFeatureValues(values);
            onFiltersLoaded(data, values, diameter);
            setIsSkeletonLoader(false);
        });
    };

    const handleEnumValuesChange = (featureId: string, enumValues: string[] | boolean) => {
        setFeaturesFilters((prevFilters) => {
            const existingFilterIndex = prevFilters.findIndex((f) => f.featureId === featureId);

            if (Array.isArray(enumValues) && enumValues.length > 0) {
                const updatedFilter = { featureId, valueType: 'enum' as const, values: enumValues };
                if (existingFilterIndex >= 0) {
                    return prevFilters.map((f, index) => (index === existingFilterIndex ? updatedFilter : f));
                }
                return [...prevFilters, updatedFilter];
            }

            if (typeof enumValues === 'boolean') {
                if (!enumValues) {
                    return prevFilters.filter((_, index) => index !== existingFilterIndex);
                }
                const updatedFilter = { featureId, valueType: 'boolean' as const, booleanValue: enumValues };
                if (existingFilterIndex >= 0) {
                    return prevFilters.map((f, index) => (index === existingFilterIndex ? updatedFilter : f));
                }
                return [...prevFilters, updatedFilter];
            }

            return prevFilters.filter((_, index) => index !== existingFilterIndex);
        });
    };

    const handleRangeChange = (featureId: string, rangeFrom: string, rangeTo: string) => {
        setFeaturesFilters((prevFilters) => getRangeFilter(featureId, rangeFrom, rangeTo, prevFilters));
    };

    const getFilterType = (filter: IFeature) => FeatureType[filter.featureType];
    const getFilterName = (filter: IFeature) => filter.labelInEnglish;

    if (isSkeletonLoader || productsLoaded === false) {
        return <OrderingTableFiltersSkeleton />;
    }

    if (filters.length === 0 && !diameterFilterExist(diameterFilter as IFeatureDiameterDto)) {
        return null;
    }

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Stack direction="row" flexWrap="wrap" alignItems="flex-start" sx={{ gap: 2 }}>
                {diameterFilterExist(diameterFilter as IFeatureDiameterDto) && (
                    <OrderingDiameterFilter diameterFilter={diameterFilter as IFeatureDiameterDto} />
                )}
                {filters.map((filter) => (
                    <Box key={filter.id} sx={filterItemSx}>
                        <Typography variant="body2" noWrap title={getFilterName(filter)} sx={filterLabelSx}>
                            {getFilterName(filter)}
                        </Typography>
                        {getFilterType(filter) === 'NumericRange' && (
                            <OrderingRangeSlider
                                min={filter.minValue}
                                max={filter.maxValue}
                                onChange={() => {}}
                                name={getFilterName(filter)}
                                featureId={filter.id}
                                onRangeChange={handleRangeChange}
                            />
                        )}
                        {(getFilterType(filter) === 'Boolean' || getFilterType(filter) === 'Enum') && (
                            <OrderingDropdownFilter
                                values={filter.availableEnumValues ?? []}
                                name={getFilterName(filter)}
                                featureId={filter.id}
                                onEnumValuesChange={handleEnumValuesChange}
                                isBoolean={getFilterType(filter) === 'Boolean'}
                                hasVariantsWithoutValue={featureCodesWithMissingValues.has(filter.code)}
                            />
                        )}
                        {(getFilterType(filter) === 'Text' || getFilterType(filter) === 'Numeric') && (
                            <OrderingDropdownFilter
                                values={filter.availableEnumValues ?? []}
                                name={getFilterName(filter)}
                                featureId={filter.id}
                                onEnumValuesChange={handleEnumValuesChange}
                                isBoolean={false}
                                hasVariantsWithoutValue={featureCodesWithMissingValues.has(filter.code)}
                            />
                        )}
                    </Box>
                ))}
            </Stack>
        </Paper>
    );
}
